import React from "react";
import {
  Image,
  Pressable,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import Ionicons from "@expo/vector-icons/Ionicons";
import { displayFont, uiFont } from "../../styles/agapellaFonts";

interface Props {
  title: string;
  body: string;
  cta: string;
  kind: "assistant" | "platform";
  isDark: boolean;
  onPress: () => void;
  testID: string;
  ctaTestID?: string;
}

/** Homepage-specific approved cards; other discovery screens retain their UI. */
export function HomeInfoCard({
  title,
  body,
  cta,
  kind,
  isDark,
  onPress,
  testID,
  ctaTestID,
}: Props) {
  const { width } = useWindowDimensions();
  const mobile = width < 768;
  const spacious = kind === "platform";
  const color = mobile ? (isDark ? "#F9F3E5" : "#314C48") : (isDark ? "#F0ECE4" : "#263B34");
  const bodyColor = mobile ? color : (isDark ? "#BCB9B4" : "#62675E");
  const buttonColor = mobile ? color : (isDark ? "#F3B280" : "#985B38");
  return (
    <View
      testID={testID}
      style={[
        styles.card,
        {
          minHeight: mobile ? (spacious ? 156 : 112) : 188,
          borderColor: mobile ? (isDark ? "#766B5D" : "#D7C9B5") : (isDark ? "#545658" : "#D8CCBA"),
          backgroundColor: isDark ? "#292D30" : "#FFFEF7",
        },
        mobile && styles.mobile,

      ]}
    >
      {mobile && <LinearGradient
        colors={isDark ? ["#323432", "#282B30"] : ["#FFFCF2", "#EDE5D8"]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={StyleSheet.absoluteFill}
      />}
      <Image
        source={
          kind === "assistant"
            ? require("../../../assets/home/agapella-approved-lotus.webp")
            : require("../../../assets/home/agapella-approved-flower.webp")
        }
        style={[styles.artwork, mobile && styles.artworkMobile]}
        resizeMode="contain"
        accessible={false}
      />
      <View style={[styles.copy, mobile && styles.copyMobile]}>
        <Text
          accessibilityRole="header"
          style={[styles.title, !mobile && styles.desktopTitle, { color }, mobile && styles.center]}
        >
          {title}
        </Text>
        <Text style={[styles.body, !mobile && styles.desktopBody, { color: bodyColor }, mobile && styles.center]}>
          {body}
        </Text>
      </View>
      <Pressable
        testID={ctaTestID}
        accessibilityRole="button"
        onPress={onPress}
        style={[
          styles.button,
          { borderColor: mobile ? "#9C9C83" : (isDark ? "#8F715B" : "#C9A68A") },
          !mobile && styles.desktopButton,
          mobile && styles.buttonMobile,

        ]}
      >
        <Text style={[styles.buttonText, !mobile && styles.desktopButtonText, { color: buttonColor }]}>{cta}</Text>
        <Ionicons name="arrow-forward" size={18} color={buttonColor} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    paddingHorizontal: 30,
    paddingVertical: 24,
    borderRadius: 24,
    borderWidth: 1,
    overflow: "hidden",
    flexDirection: "row",
    gap: 28,
    alignItems: "center",
  },
  desktopTitle: { fontSize: 28, lineHeight: 36 },
  desktopBody: { fontSize: 18, lineHeight: 28 },
  desktopButton: { width: 208, minHeight: 48, paddingHorizontal: 16, flexShrink: 0, borderRadius: 16, borderWidth: 1, backgroundColor: "transparent" },
  desktopButtonText: { fontSize: 16, lineHeight: 24 },
  mobile: { padding: 24, flexDirection: "column", gap: 18 },
  artwork: { width: 44, height: 44 },
  artworkMobile: { width: 52, height: 52 },
  copy: { flex: 1, gap: 8 },
  copyMobile: { flex: undefined, width: "100%" },
  title: { ...displayFont(), fontSize: 24, lineHeight: 32 },
  body: { ...uiFont(), fontSize: 14, lineHeight: 21 },
  center: { textAlign: "center" },
  button: {
    minHeight: 44,
    borderRadius: 24,
    borderWidth: 1.5,
    paddingHorizontal: 24,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    backgroundColor: "rgba(255,249,226,0.08)",
  },
  buttonMobile: { alignSelf: "center" },
  buttonText: { ...uiFont(700), fontSize: 14, lineHeight: 20 },
});
